import useRouteInfo from "@/app/hooks/useRouteInfo";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import TableSkeletonComponent from "@/app/components/ui/LoaderComponent";
import { useRouter } from "@/app/hooks/useRouter";
import { useEffect, useState } from "react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import { useLoader } from "@/app/hooks/useLoader";
import { Disclosure } from "@headlessui/react";
import { useAISummaryAPI } from "@/app/hooks/api_hooks/emr/useAISummaryAPI";
import { useAIGenerateSummaryAPI } from "@/app/hooks/api_hooks/emr/useAIGenerateSummaryAPI";
import { FaRobot, FaThumbsUp, FaThumbsDown } from "react-icons/fa";
import { FiLoader } from "react-icons/fi";
import { useListEMRUserAndPetAPI } from "@/app/hooks/api_hooks/emr/useListEMRUserAndPetAPI";
import { IEMRListForUserAndPetResponseData } from "@/api_framework/api_modals/EMRs";
import { IClickLogBody, useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";
import { useAppSelector } from "@/app_redux/hooks/root_hook";

const AISummaryTab = () => {
  const { NO_APPOINTMENTS } = useImageConstants();
  const { getRouteKey } = useRouter();
  const { routeState } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id"));
  const { petDetails, customerId } = routeState.state as ICustomerProfileState;

  const { execute: fetchAISummary, aiSummary } = useAISummaryAPI();
  const { execute: generateAISummary } = useAIGenerateSummaryAPI();
  const { execute: fetchEMRList, vmsEMRs: emrList } = useListEMRUserAndPetAPI();
  const { execute: clickLogAPI } = useClickLogAPI();

  useEffect(() => {
    if (petDetails?.id && customerId) {
      fetchAISummary(petDetails.id);
      fetchEMRList(customerId, petDetails.id);
    }
  }, [petDetails?.id, customerId]);

  const isAISummaryLoader = useLoader("isAISummaryFetchInProgress");
  const isGeneratingLoader = useLoader("isAIGenerateSummaryInProgress");
  const isEmrFetching = useLoader("isEMRFetchInProgress");

  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [feedbackState, setFeedbackState] = useState<"thumbs_up" | "thumbs_down" | null>(null);

  const userLogInfo = useAppSelector((state) => state.auth?.user?.email);

  useEffect(() => {
    if (!isAISummaryLoader && !isEmrFetching) {
      setIsDataLoaded(true);
    }
  }, [isAISummaryLoader, isEmrFetching]);

  const handleGenerateSummary = async () => {
    const constructedBody: IClickLogBody = {
      event_type: "ai_summary_generate",
      scr_id: "ai_summary_feedback",
      timestamp: Date.now(),
      client_id: customerId,
      pet_id: petDetails?.id,
      vms_user_id: userLogInfo,
      emr_id: latestCompletedEMR?.emr_id,
    };
    clickLogAPI(constructedBody);

    if (petDetails?.id) {
      const response = await generateAISummary(petDetails.id);
      if (response) {
        // Refresh the summary data after successful generation
        fetchAISummary(petDetails.id);
      }
    }
  };

  const latestCompletedEMR = emrList
    ?.filter((emr: IEMRListForUserAndPetResponseData) => emr.emr_status === "COMPLETED")
    .sort(
      (a: IEMRListForUserAndPetResponseData, b: IEMRListForUserAndPetResponseData) => b.created_at - a.created_at,
    )[0];

  const handleClickLog = (response: string) => {
    const constructedBody: IClickLogBody = {
      event_type: "ai_summary_feedback",
      event_action: response,
      scr_id: "ai_summary_feedback",
      timestamp: Date.now(),
      client_id: customerId,
      pet_id: petDetails?.id,
      vms_user_id: userLogInfo,
      emr_id: latestCompletedEMR?.emr_id,
      summary_id: aiSummary?.data?.id?.toString(),
    };
    clickLogAPI(constructedBody);
  };

  return (
    <>
      <div className="py-2">
        <div className="border rounded-lg bg-white">
          <Disclosure defaultOpen={true}>
            {({ open }) => (
              <>
                <Disclosure.Button className="px-2 md:px-2 lg:px-2  py-1.5 md:py-1.5 items-center flex w-full justify-between rounded-lg bg-[#808DA01A] text-left text-theme-sm font-medium  focus:outline-none">
                  <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        {/* <FaRobot className="text-gray-600" /> */}
                        <span className="text-theme-sm text-[#000]">Summary</span>
                        {isDataLoaded && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleGenerateSummary();
                            }}
                            disabled={isGeneratingLoader}
                            className="relative inline-flex h-7 min-w-[92px] items-center justify-center rounded-[5px] bg-white px-2 py-1.5 text-theme-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-300 whitespace-nowrap shrink-0 transition-colors duration-150 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:ring-gray-200 disabled:cursor-not-allowed"
                          >
                            {isGeneratingLoader && (
                              <span className="absolute inset-0 flex items-center justify-center">
                                <FiLoader className="h-4 w-4 animate-spin text-gray-600" />
                              </span>
                            )}
                            {!isGeneratingLoader && <span>Generate</span>}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-theme-xs underline shrink-0">
                    {open ? "Hide Details" : "Show Details"}
                    <ChevronDownIcon className={`${open ? "rotate-180 transform" : ""} h-4 w-4`} />
                  </div>
                </Disclosure.Button>
                <Disclosure.Panel className="px-2 py-2 bg-white text-theme-xs text-gray-500">
                  <div className="bg-white w-[100%]">
                    {isAISummaryLoader ? (
                      <TableSkeletonComponent columnLength={1} bodyLength={3} />
                    ) : (
                      <div className="max-h-[400px] md:max-h-[550px] lg:max-h-[650px] overflow-y-auto">
                        {aiSummary?.data?.summary && aiSummary.data.summary.length > 0 ? (
                          <div className="bg-white rounded-xl p-2 md:p-2 space-y-2">
                            <div className="mb-3">
                              <div className="hidden flex items-center gap-2 mb-2">
                                <FaRobot className="text-gray-600 text-lg" />
                                <h3 className="text-theme-xs font-mono-DIS uppercase text-gray-900">AI-Generated Summary</h3>
                              </div>
                              <p className="text-theme-xs text-gray-600  font-mono-DIS">
                                AI-powered analysis of {petDetails?.name}'s medical history and recent visits.
                              </p>
                            </div>

                            <div className="space-y-2">
                              {aiSummary.data.summary.map((summaryPoint, index) => (
                                <div
                                  key={index}
                                  className="flex items-start p-3-DIS bg-blue-50-DIS rounded-lg-DIS border-l-4-DIS border-blue-400-DIS"
                                >
                                  <div className="w-6 h-6 rounded-full flex items-top justify-center shrink-0 ">
                                    <span className="text-theme-xs font-semibold">
                                      {index + 1}
                                    </span>
                                  </div>

                                  <p className="text-theme-xs text-gray-800 leading-relaxed break-words whitespace-pre-line">
                                    {summaryPoint}
                                  </p>
                                </div>
                              ))}
                            </div>

                            <div className=" pt-2 border-t border-gray-200">
                              <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between text-theme-xs text-gray-500">
                                <div className="flex flex-col gap-1">
                                  {/* <span>Last EMR ID: {aiSummary.data.checkpoint_emr_id}</span> */}
                                  {/* <span>Created: {new Date(aiSummary.data.created_at).toLocaleDateString()}</span> */}
                                  <span className="text-theme-xs">AI generated; there may be mistakes.</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-theme-xs text-gray-600 ">Was this helpful?</span>
                                  <button
                                    onClick={() => handleClickLog("helpful")}
                                    className={`p-2 rounded-full transition-all duration-200 ${feedbackState === "thumbs_up"
                                      ? "bg-green-100 text-green-600 hover:bg-green-200"
                                      : "bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-green-600"
                                      }`}
                                    title="Thumbs up"
                                  >
                                    <FaThumbsUp className="w-3 h-3" />
                                  </button>
                                  <button
                                    onClick={() => handleClickLog("not-helpful")}
                                    className={`p-2 rounded-full transition-all duration-200 ${feedbackState === "thumbs_down"
                                      ? "bg-red-100 text-red-600 hover:bg-red-200"
                                      : "bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-red-600"
                                      }`}
                                    title="Thumbs down"
                                  >
                                    <FaThumbsDown className="w-3 h-3" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <NoDataFoundComponent
                            imageURL={NO_APPOINTMENTS}
                            message="No AI Summary Available"
                            description="No AI-generated summary is available for this pet at the moment."
                            className="w-[50px] md:w-[60px] xl:w-[70px] 2xl:w-[80px]"
                          />
                        )}
                      </div>
                    )}
                  </div>
                </Disclosure.Panel>
              </>
            )}
          </Disclosure>
        </div>
      </div>
    </>
  );
};

export default AISummaryTab;
